import React from 'react';
import { Link } from 'react-router-dom';
import { StaticPageLayout } from '../StaticPageLayout';
import styles from './ScienceOfTime.module.scss';
import { AnalogClock } from '../../AnalogClock/AnalogClock';
import { TaskState, TaskWithState } from '../../../types';
import { usePageSEO } from '../../../hooks/usePageSEO';

export const ScienceOfTime: React.FC = () => {
  usePageSEO({
    title: "The Science of Kids & Time - MyMinu",
    description: "Why '5 more minutes' means nothing to a 4-year-old. Discover the developmental science behind visual routines.",
    url: window.location.origin + "/science-of-time"
  });

  // Static visual for the example
  const exampleTime = new Date();
  exampleTime.setHours(10, 0, 0, 0);
  
  const exampleTask: TaskWithState = {
    id: 'science-1',
    profileId: 'science-profile',
    label: 'Playtime',
    icon: '🧸',
    color: '#D65A4A',
    start: '09:50',
    end: '10:05',
    startTime: new Date(exampleTime.getTime() - 1000 * 60 * 10),
    endTime: new Date(exampleTime.getTime() + 1000 * 60 * 5), // 5 min left
    opacity: 1,
    isCompleted: false,
    state: TaskState.ACTIVE,
    shouldPulse: false
  };

  return (
    <StaticPageLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>The Science of Kids & Time</h1>
          <p className={styles.subtitle}>
            Why "5 more minutes" means nothing to a 4-year-old, and how to fix it.
          </p>
        </div>

        <section className={styles.section}>
          <h2>The Developmental Timeline</h2>
          <div className={styles.timelineGrid}>
            <div className={styles.milestone}>
              <div className={styles.ageTag}>Ages 3–5</div>
              <h3>The Event Thinkers</h3>
              <p>
                Toddlers understand sequences (Bath comes after Dinner), but have <strong>zero</strong> concept of duration. 
                Telling them "10 minutes" is like speaking a foreign language. They rely entirely on cues.
              </p>
            </div>
            <div className={styles.milestone}>
              <div className={styles.ageTag}>Ages 5–7</div>
              <h3>The Emerging Readers</h3>
              <p>
                They might recognize numbers on a clock, but still can't "feel" how long a task takes. 
                They need visual aids to bridge the gap between the number "5" and the feeling of time passing.
              </p>
            </div>
            <div className={styles.milestone}>
              <div className={styles.ageTag}>Ages 8+</div>
              <h3>The Abstract Thinkers</h3>
              <p>
                They are beginning to understand hours and minutes, but complex planning still overwhelms them. 
                Visual structure helps them manage homework and chores independently.
              </p>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h2>Why Transitions Cause Meltdowns</h2>
          <div className={styles.twoColumn}>
            <div>
              <p>
                Research shows that for young children, a sudden transition (like being told to stop playing immediately) 
                can trigger a <strong>fight-or-flight response</strong>. Their brain perceives the abrupt change as a threat.
              </p>
              <p>
                <strong>The "Alarm" Problem:</strong> Traditional timers with loud buzzers often increase anxiety. 
                The child spends the whole time worrying about the noise rather than preparing to stop.
              </p>
              <p>
                <strong>The Visual Solution:</strong> A shrinking wedge (like MyMinu's) is a gentle, continuous signal. 
                It allows the child to <em>anticipate</em> the end, preparing their brain for the shift.
              </p>
            </div>
            <div className={styles.visualExample}>
              <div className={styles.clockWrapper}>
                <AnalogClock
                  currentTime={exampleTime}
                  tasks={[exampleTask]}
                  nextTask={{ task: null, countdownText: '', timeLeft: 0 }}
                  onChipClick={() => {}}
                  themeId="playful_focus"
                  showPastTasks={false}
                />
              </div>
              <p className={styles.caption}>Seeing the red wedge shrink helps them prepare.</p>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h2>Gamifying the Routine</h2>
          <p>
            One of the most effective strategies supported by child psychology is "Gamified Cooperation". 
            Instead of a parent vs. child power struggle, it becomes Child vs. The Clock.
          </p>
          <ul className={styles.tipsList}>
            <li>
              <strong>"Beat the Timer":</strong> Can you put your shoes on before the red wedge disappears?
            </li>
            <li>
              <strong>First/Then:</strong> Visualizing that "First we Clean Up, Then we go to the Park" helps motivate compliance.
            </li>
            <li>
              <strong>Ownership:</strong> Letting the child tap the "Done" button gives them a dopamine hit of success.
            </li>
          </ul>
        </section>

        <div className={styles.cta}>
          <h3>Ready to make time visible?</h3>
          <Link to="/register" className={styles.primaryButton}>
            Start Your Free Routine
          </Link>
        </div>
      </div>
    </StaticPageLayout>
  );
};
